import { ClientBookingDataTransferType, ClientBookingDetail } from "@/redux/apis/clientBooking/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { isFixedRate } from "./bookingPayload";
import { ClientBookingState, ClientBookingVehicle } from "./types";

/**
 * The minimal, serialisable trip facts a re-book is built from — a subset of the finished booking
 * (route + vehicle/transfer + passenger/notes/rate) carried by the guest confirmation snapshot and
 * (later) the dashboard's re-book action. Deliberately narrower than `ClientBookingState`: it holds
 * only what a re-book reuses, not the whole draft.
 */
export interface RebookSource {
  /** Ordered `[pickup, ...stops, destination]` from the finished trip. */
  locations: PointsOfInterest[];
  vehicle?: ClientBookingVehicle | null;
  transferType?: { id: number; label?: string; type?: string } | null;
  numOfPassengers?: number;
  numOfBags?: number;
  additionalInfo?: string;
  jobRate?: string;
  bookedForDuration?: number;
  /** The booked total of a fixed-rate booking, which the price override is. Seeds the edit panel. */
  fixedPrice?: number;
}

/**
 * How to seed the next booking from a finished one (mirrors app-customer SuccessScreen):
 * - `return`   — reverse the trip (pickup ↔ dropoff), keep middle stops in original order.
 * - `duplicate` — same trip, same order.
 * - `fresh`    — a blank booking (mobile's "Interstate" opens a new trip, no prefill).
 */
export type RebookMode = "return" | "duplicate" | "fresh";

const bookedTotal = (detail: ClientBookingDetail): number | undefined => {
  const amount = Number(detail.estimated_payment ?? undefined);
  return Number.isFinite(amount) && amount > 0 ? amount : undefined;
};

/**
 * Build a partial booking draft from a finished trip. `return`/`duplicate` carry the vehicle, transfer
 * type, passenger count, bags, notes, job rate and duration, but ALWAYS clear the pickup datetime,
 * flight number, extras and every payment field — the next trip needs a fresh date/quote and its own
 * card. `fresh` returns an essentially empty draft. Meant to be merged over the slice's `initialState`
 * before stashing/dispatching.
 */
/**
 * Map a single-booking detail (GET /v2/cust/booking/{jobId}) to a `RebookSource`. The detail carries
 * the numeric vehicle id (`vehicle.type`), the transfer-type LABEL and — since DEV-1636 — its numeric
 * id. The type is resolved against the client job-data (`transfer_types`) by id first, so a renamed
 * type still resolves, and by label for a backend predating the id. No match ⇒ `transferType` is left
 * null: a RE-BOOK is a NEW booking, and one must not be placed on a type the client is not offered, so
 * the flow makes the user reselect. The view/edit replay keeps the booking's own type instead — see
 * mapBookingDetailToViewDraft. Waypoints are normalised into the POI shape; missing fields default to
 * "" / [] so the draft is always valid.
 */
export const mapBookingDetailToRebookSource = (
  detail: ClientBookingDetail,
  transferTypes: ClientBookingDataTransferType[] = []
): RebookSource => {
  const locations: PointsOfInterest[] = (detail.waypoints ?? []).map((wp) => ({
    uuid: wp.uuid ?? "",
    vendor: wp.vendor ?? "",
    vendor_uuid: wp.vendor_uuid ?? "",
    vendor_address: wp.vendor_address ?? "",
    address: wp.address || wp.formatted_address || "",
    lat: wp.lat,
    lng: wp.lng,
    city: wp.city ?? "",
    postcode: wp.postcode ?? "",
    state: wp.state ?? "",
    state_code: wp.state_code ?? "",
    country: wp.country ?? "",
    country_code: wp.country_code ?? "",
    rm_label: wp.rm_label ?? "",
    rm_type: wp.rm_type ?? "",
    rm_tags: wp.rm_tags ?? [],
    rm_exclusions: wp.rm_exclusions ?? [],
  }));

  const vehicle: ClientBookingVehicle | null = detail.vehicle
    ? { vehicle_id: detail.vehicle.type, name: detail.vehicle.type_friendly }
    : null;

  const label = detail.transfer_type?.trim();
  const id = detail.transfer_type_id ?? null;
  const match =
    (id != null ? transferTypes.find((t) => Number(t.id) === id) : undefined) ??
    (label ? transferTypes.find((t) => t.label === label) : undefined);
  // Carry the transfer type's rate `type` (journey/hourly/daily) so the edit panel + validation know
  // whether a duration is required, consistent with a freshly-selected transfer type.
  const transferType = match ? { id: Number(match.id), label: match.label, type: match.type } : null;

  // Hourly/daily rates carry the duration on either field (see BookingResource::getJobData);
  // journey_rate leaves both 0 → undefined (buildRebookDraft carries it as-is).
  const bookedForDuration = detail.booked_for_duration || detail.hourly_journey_minutes || undefined;

  return {
    locations,
    vehicle,
    transferType,
    numOfPassengers: detail.number_of_passengers,
    numOfBags: detail.number_of_bags ?? undefined,
    additionalInfo: detail.additional_info,
    jobRate: detail.journey_type,
    bookedForDuration,
    // The override is the total the client pays, fees included. `estimated_payment` is the booked
    // total; `vehicle.cost` is the base fare without levy and fees on a job booked before DEV-1969, and
    // `total` includes anything added at close. Hidden pricing leaves it undefined.
    fixedPrice: isFixedRate(detail.journey_type) ? bookedTotal(detail) : undefined,
  };
};

export const buildRebookDraft = (source: RebookSource, mode: RebookMode): Partial<ClientBookingState> => {
  // A re-book always starts a new date/quote and captures a fresh payment — never carry these forward.
  const cleared: Partial<ClientBookingState> = {
    pickupDatetime: null,
    pickupFlightNumber: null,
    pickupFlightDelayMinutes: null,
    pickupFlightLandingUsed: null,
    // The DEPARTURE flight goes with them (DEV-1487): a re-book picks a new date, so the flight caught
    // on the old one is not the flight caught on this one.
    departureFlightNumber: null,
    departureTerminalSource: null,
    pickupTimeSource: null,
    extras: [],
    paymentMethod: null,
    guestPaymentMethodId: null,
    guestPaymentMethodCountry: null,
    guestPaymentMethodCard: null,
  };

  if (mode === "fresh") {
    // "Interstate" opens a blank booking (mobile parity) — clear the route and every carried field too.
    return {
      pickup: null,
      destination: null,
      stops: [],
      vehicle: null,
      transferType: null,
      numOfPassengers: undefined,
      numOfBags: undefined,
      additionalInfo: undefined,
      jobRate: undefined,
      bookedForDuration: undefined,
      fixedPrice: undefined,
      ...cleared,
    };
  }

  const { locations } = source;
  const first = locations[0] ?? null;
  const last = locations.length > 0 ? locations[locations.length - 1] : null;
  // Middle stops only, in original order — the reverse (`return`) keeps them in the same sequence.
  const stops: (PointsOfInterest | null)[] = locations.slice(1, -1);

  return {
    pickup: mode === "return" ? last : first,
    destination: mode === "return" ? first : last,
    stops,
    vehicle: source.vehicle ?? null,
    transferType: source.transferType ?? null,
    numOfPassengers: source.numOfPassengers,
    numOfBags: source.numOfBags,
    additionalInfo: source.additionalInfo,
    jobRate: source.jobRate,
    bookedForDuration: source.bookedForDuration,
    fixedPrice: source.fixedPrice,
    ...cleared,
  };
};
